import { fetchClient } from "@/api/api";
import type {
	forgetPasswordRequestSchemaInput,
	forgetPasswordRequestSchemaOutput,
	forgetPasswordSchemaInput,
	forgetPasswordSchemaOutput,
	getOtpSchemaOutput,
	getOtpSchemaQuery,
	logOutSchemaOutput,
	loginSchemaInput,
	loginSchemaOutput,
	signupSchemaInput,
	signupSchemaOutput,
	verifyOtpSchemaInput,
	verifyOtpSchemaOutput,
} from "@/api/custom_types";
import { useTranslation } from "@/hooks";
import useApiError from "@/hooks/useApiError";
import { currentUserAtom } from "@/store/login";
import { otpEmailAtom } from "@/store/otp";
import { telegramBackUrlAtom } from "@/store/telegram";
import type { ApiError } from "@/utils/fetching";
import { type CustomMutationHook, queryClient } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useSetAtom } from "jotai";
import { AUTH_SERVICE_KEYS } from "./keys";

export const useLogin: CustomMutationHook<loginSchemaInput, loginSchemaOutput> =
	() => {
		const setCurrentUser = useSetAtom(currentUserAtom);
		const setOtpEmail = useSetAtom(otpEmailAtom);
		const { toastApiError } = useApiError();
		const { t } = useTranslation();
		const navigate = useNavigate();

		return useMutation<loginSchemaOutput, ApiError, loginSchemaInput>({
			mutationKey: AUTH_SERVICE_KEYS.useLogin(),
			mutationFn: async (body) => {
				const { data, error } = await fetchClient.POST("/v1/fc/login", {
					body,
					throwOnError: true,
				});
				if (error) {
					// Attach email to error for NotConfirmedEmail handling
					(error as any).email = body.email;
					throw error;
				}
				return data;
			},
			onSuccess: (response) => {
				setCurrentUser(response);
				if (response.role === "MARKETER") {
					toast.error(t("auth.login.noAccess"));
				} else {
					const backUrl = getDefaultStore().get(telegramBackUrlAtom);
					if (backUrl) {
						getDefaultStore().set(telegramBackUrlAtom, null);
						navigate({ ...backUrl });
					} else {
						navigate({ to: "/dashboard" });
					}
				}
			},
			onError: (error, variables) => {
				// Check if error is NotConfirmedEmail
				if (
					error.body &&
					typeof error.body === "object" &&
					"type" in error.body &&
					error.body.type === "NotConfirmedEmail"
				) {
					const email = variables.email;
					if (email) {
						setOtpEmail(email);
						navigate({ to: "/otp" });
						return;
					}
				}
				toastApiError(error);
			},
		});
	};

export const useLogout: CustomMutationHook<void, logOutSchemaOutput> = () => {
	const setCurrentUser = useSetAtom(currentUserAtom);
	const navigate = useNavigate();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: AUTH_SERVICE_KEYS.useLogout(),
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST("/v1/fc/logout", {
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: async () => {
			setCurrentUser(null);
			queryClient.clear();
			navigate({ to: "/login" });
			toast.success(t("auth.logoutSuccessMessage"));
		},
	});
};
export const useSignup: CustomMutationHook<
	signupSchemaInput,
	signupSchemaOutput
> = () => {
	const { toastApiError } = useApiError();
	return useMutation<signupSchemaOutput, ApiError, signupSchemaInput>({
		mutationKey: AUTH_SERVICE_KEYS.useSignup(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/signup", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetOtp: CustomMutationHook<
	getOtpSchemaQuery,
	getOtpSchemaOutput
> = () => {
	return useMutation<getOtpSchemaOutput, ApiError, getOtpSchemaQuery>({
		mutationKey: AUTH_SERVICE_KEYS.useGetOtp(),
		mutationFn: async (query) => {
			const { data, error } = await fetchClient.POST("/v1/fc/otp", {
				params: {
					query,
				},
				throwOnError: true,
			});

			if (error) {
				throw error;
			}
			return data;
		},
	});
};

export const useVerifyOtp: CustomMutationHook<
	verifyOtpSchemaInput,
	verifyOtpSchemaOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<verifyOtpSchemaOutput, ApiError, verifyOtpSchemaInput>({
		mutationKey: AUTH_SERVICE_KEYS.useVerifyOtp(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/otp/verify", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useForgetPasswordRequest: CustomMutationHook<
	forgetPasswordRequestSchemaInput,
	forgetPasswordRequestSchemaOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		forgetPasswordRequestSchemaOutput,
		ApiError,
		forgetPasswordRequestSchemaInput
	>({
		mutationKey: AUTH_SERVICE_KEYS.useForgetPasswordRequest(),
		mutationFn: async (query) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/forget-password-request",
				{
					params: {
						query,
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useForgetPassword: CustomMutationHook<
	forgetPasswordSchemaInput,
	forgetPasswordSchemaOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		forgetPasswordSchemaOutput,
		ApiError,
		forgetPasswordSchemaInput
	>({
		mutationKey: AUTH_SERVICE_KEYS.useForgetPassword(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/forget-password", {
				body,
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};
